<script setup lang="ts">
import type { PropType } from 'vue';

import { NegotiationOutcome } from '~/apiClient';
import { type AuctionItemEntityWithNegotiation } from '~/types/sale.type';

// PROPS
const { auction } = defineProps({
    auction: { type: Object as PropType<AuctionItemEntityWithNegotiation>, required: true },
    class: { type: [String, Object, Array], default: undefined },
    embedded: { type: Boolean, default: false },
});

// COMPOSABLES
const { tt, tn } = useTypedI18n();
const { negotiation, isBuyer, isEnded, isAccepted, isExpired, isUnderSellerReview } = useAuctionNegotiation(() => auction);
const { trackNegotiationUpdateProposal, trackNegotiationAccept, trackNegotiationBuyerStopProposal, trackNegotiationSellerDecline } = useTracking();

// DATA
const panelRef = ref<HTMLElement>();

const isProposalModalOpen = ref<boolean>(false);
const isAcceptModalOpen = ref(false);
const isBuyerStopProposalsModalOpen = ref(false);
const isSellerDeclineModalOpen = ref(false);

const endedNotAcceptedNotice = computed(() => {
    if (isExpired.value) return tt('auctionNegotiation.expiredNotice');
    if (negotiation.value.outcome === NegotiationOutcome.DeclinedBySeller)
        return tt(isBuyer.value ? 'auctionNegotiation.sellerDeclineOfferNotice' : 'auctionNegotiation.youDeclineOfferNotice');
});

const buttonUi = buttonResponsiveSizeUi('md', 'xl');

// METHODS
const onUpdateProposalClick = (): void => {
    isProposalModalOpen.value = true;
    trackNegotiationUpdateProposal(isBuyer.value);
};

const onAcceptClick = (): void => {
    isAcceptModalOpen.value = true;
    trackNegotiationAccept(isBuyer.value);
};

const onBuyerStopProposalClick = (): void => {
    isBuyerStopProposalsModalOpen.value = true;
    trackNegotiationBuyerStopProposal();
};

const onSellerDeclineClick = (): void => {
    isSellerDeclineModalOpen.value = true;
    trackNegotiationSellerDecline();
};
</script>

<template>
    <div ref="panelRef" class="bg-white" :class="{ 'overflow-clip rounded-xl border border-neutral-300': !embedded }">
        <template v-if="isEnded">
            <div class="space-y-3 p-4 lg:space-y-4 lg:p-6" :class="isAccepted ? 'bg-success-50' : 'bg-neutral-50'">
                <p class="text-xs font-extrabold tracking-widest uppercase lg:text-sm" :class="isAccepted ? 'text-success-600' : 'text-default'">
                    {{ tt(`sdp.Auction.${isAccepted ? 'auctionStatus.wonAuction' : 'state.Expired'}`) }}
                </p>

                <p v-if="negotiation.closeAmount" class="text-success-800 text-2xl font-semibold lg:text-5xl">
                    {{ tn(negotiation.closeAmount, 'currency') }}
                </p>
                <AdpNegotiationDualAmounts v-else :auction="auction" />
            </div>

            <div v-if="endedNotAcceptedNotice" class="flex items-center gap-2 border-t border-neutral-300 p-4">
                <UIcon class="text-dimmed shrink-0" name="lucide:info" />
                <p class="text-dimmed text-xs lg:text-sm">{{ endedNotAcceptedNotice }}</p>
            </div>

            <SdpSalePanelOrderSection v-if="isAccepted && isBuyer" />
        </template>

        <template v-else>
            <div class="bg-primary-50 space-y-3 p-4 lg:space-y-4 lg:p-6">
                <div class="flex justify-between">
                    <div class="space-y-1">
                        <p class="text-xs font-extrabold tracking-widest uppercase lg:text-sm">
                            {{ tt(`sdp.Auction.state.Expired`) }}
                        </p>
                        <p class="text-dimmed text-xs lg:text-sm">
                            {{ tt('auctionNegotiation.numberOfBids', { count: auction.numberOfBids }) }}
                        </p>
                    </div>
                    <p class="text-error-600 text-xs font-extrabold tracking-widest uppercase lg:text-sm">
                        {{ tt('auctionNegotiation.underNegotiation') }}
                    </p>
                </div>

                <AdpNegotiationDualAmounts :auction="auction" />
            </div>

            <div class="space-y-3 border-t border-neutral-300 p-4 lg:space-y-4 lg:p-6">
                <AdpNegotiationMainNotice :auction="auction" />

                <template v-if="isBuyer">
                    <template v-if="!isUnderSellerReview">
                        <UButton
                            class="mt-4"
                            size="md"
                            :ui="buttonUi"
                            color="secondary"
                            :label="tt('auctionNegotiation.updateOffer')"
                            block
                            @click="onUpdateProposalClick"
                        />
                        <UButton size="md" :ui="buttonUi" :label="tt('auctionNegotiation.acceptRequest')" block @click="onAcceptClick" />
                        <UButton
                            color="error"
                            size="md"
                            :ui="buttonUi"
                            variant="link"
                            class="px-0"
                            block
                            :label="tt('auctionNegotiation.buyerStopProposals')"
                            @click="onBuyerStopProposalClick"
                        />
                    </template>
                </template>

                <template v-else>
                    <UButton
                        v-if="!isUnderSellerReview"
                        class="mt-4"
                        size="md"
                        :ui="buttonUi"
                        color="secondary"
                        :label="tt('auctionNegotiation.updateRequest')"
                        block
                        @click="onUpdateProposalClick"
                    />
                    <UButton size="md" :ui="buttonUi" :label="tt('auctionNegotiation.acceptOffer')" block @click="onAcceptClick" />
                    <UButton
                        color="error"
                        size="md"
                        :ui="buttonUi"
                        variant="link"
                        class="px-0"
                        block
                        :label="tt('auctionNegotiation.sellerDecline')"
                        @click="onSellerDeclineClick"
                    />
                </template>
            </div>
        </template>
    </div>

    <AdpNegotiationProposalModal v-model="isProposalModalOpen" :auction="auction" />
    <AdpNegotiationAcceptModal v-model="isAcceptModalOpen" :auction="auction" />
    <AdpNegotiationBuyerStopProposalsModal v-model="isBuyerStopProposalsModalOpen" :auction="auction" />
    <AdpNegotiationSellerDeclineModal v-model="isSellerDeclineModalOpen" :auction="auction" />
</template>
